import { ReactElement, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Typography,
  Paper,
  Chip,
  Alert,
  Snackbar,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import LoadingSpinner from "../components/LoadingSpinner";
import DeleteConfirmationDialog from "../components/DeleteConfirmationDialog";
import { auth, db } from "../firebase";
import { doc, getDoc } from "firebase/firestore";
import { getDefaultDecimalPlaces } from "../utils/currencyDefaults";

// Hooks
import { useCurrencies } from "./CurrencySettings/hooks/useCurrencies";
import { useDefaultCurrency } from "./CurrencySettings/hooks/useDefaultCurrency";
import { useCurrencyAccounts } from "./CurrencySettings/hooks/useCurrencyAccounts";
import { useCurrencyUIState } from "./CurrencySettings/hooks/useCurrencyUIState";
import { useCurrencyHelpers } from "./CurrencySettings/hooks/useCurrencyHelpers";
import { useCurrencyHandlers } from "./CurrencySettings/hooks/useCurrencyHandlers";
import { useCurrencyDragAndDrop } from "./CurrencySettings/hooks/useCurrencyDragAndDrop";
import { useWorkspace } from "./WorkspaceSettings/hooks/useWorkspace";
import { useWorkspaceMembers } from "./WorkspaceSettings/hooks/useWorkspaceMembers";
import { useWorkspaceSettings } from "./WorkspaceSettings/hooks/useWorkspaceSettings";
import { usePermissionHelpers } from "./Activity/hooks/usePermissionHelpers";

// Components
import AddCurrencyDialog from "./CurrencySettings/components/AddCurrencyDialog";
import EditRateDialog from "./CurrencySettings/components/EditRateDialog";
import EditDecimalPlacesDialog from "./CurrencySettings/components/EditDecimalPlacesDialog";
import RefreshRateDialog from "./CurrencySettings/components/RefreshRateDialog";
import DefaultCurrencyDialog from "./CurrencySettings/components/DefaultCurrencyDialog";
import CurrencyTable from "./CurrencySettings/components/CurrencyTable";

interface CurrencySettingsProps {
  workspaceId: string;
}

function CurrencySettings({ workspaceId }: CurrencySettingsProps): ReactElement {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // Workspace and permissions (for currency permission checks)
  const { workspace, isOwner } = useWorkspace({ workspaceId });
  const { permissions } = useWorkspaceSettings({ workspaceId });
  const { currentUserRole } = useWorkspaceMembers({ workspaceId, workspace });
  const {
    canAddCurrency,
    canChangeDefaultCurrency,
    canChangeCurrencyRateDecimalPlaces,
  } = usePermissionHelpers({
    permissions,
    currentUserRole: currentUserRole ?? "user",
    workspaceOwnerId: workspace?.ownerId,
  });
  // Ensure workspace owner can always change default currency (recover visibility if permission/role load late)
  const canEditDefaultCurrency = (): boolean => canChangeDefaultCurrency() || isOwner;
  const canEditDefaultDecimals = (): boolean => canChangeCurrencyRateDecimalPlaces() || isOwner;

  // Data fetching hooks
  const { defaultCurrency, setDefaultCurrency, isLoading: isLoadingDefaultCurrency } = useDefaultCurrency({ workspaceId });
  const { currencies, loading, setCurrencies } = useCurrencies({ workspaceId, defaultCurrency: defaultCurrency || "USD" });
  const { accounts, setAccounts } = useCurrencyAccounts({ workspaceId });

  // UI state hook
  const uiState = useCurrencyUIState();

  // Track when user is dragging a currency (mobile reorder) for auto-scroll near edges
  const [isDraggingCurrency, setIsDraggingCurrency] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Auto-scroll when dragging near top/bottom so user can reorder to off-screen positions
  useEffect(() => {
    if (!isMobile || !isDraggingCurrency) return;
    const container = scrollContainerRef.current;
    if (!container) return;

    const EDGE_ZONE = 0.25; // top/bottom 25% of visible area
    const SCROLL_STEP = 12;

    const handlePointerMove = (e: PointerEvent) => {
      const rect = container.getBoundingClientRect();
      const y = e.clientY - rect.top;
      const relativeY = y / rect.height;
      if (relativeY < EDGE_ZONE) {
        container.scrollTop -= SCROLL_STEP;
      } else if (relativeY > 1 - EDGE_ZONE) {
        container.scrollTop += SCROLL_STEP;
      }
    };

    document.addEventListener("pointermove", handlePointerMove, { passive: true });
    return () => document.removeEventListener("pointermove", handlePointerMove);
  }, [isMobile, isDraggingCurrency]);

  // Fetch default currency's decimal places for display
  const [defaultCurrencyDecimalPlaces, setDefaultCurrencyDecimalPlaces] = useState<number>(2);
  
  useEffect(() => {
    if (!defaultCurrency) return;
    
    // Immediately set to default value based on currency code to avoid showing wrong value
    setDefaultCurrencyDecimalPlaces(getDefaultDecimalPlaces(defaultCurrency));
    
    if (!workspaceId) return;
    
    const fetchDefaultCurrencyDecimalPlaces = async () => {
      try {
        const currencyRef = doc(db, "workspaces", workspaceId, "currencies", defaultCurrency);
        const currencyDoc = await getDoc(currencyRef);
        
        if (currencyDoc.exists()) {
          const data = currencyDoc.data();
          setDefaultCurrencyDecimalPlaces(data.decimalPlaces !== undefined ? data.decimalPlaces : getDefaultDecimalPlaces(defaultCurrency));
        } else {
          setDefaultCurrencyDecimalPlaces(getDefaultDecimalPlaces(defaultCurrency));
        }
      } catch (error) {
        console.error("Error fetching default currency decimal places:", error);
        setDefaultCurrencyDecimalPlaces(getDefaultDecimalPlaces(defaultCurrency));
      }
    };
    
    fetchDefaultCurrencyDecimalPlaces();
  }, [workspaceId, defaultCurrency, uiState.editDecimalPlacesDialogOpen]); // Refresh when dialog closes

  // Helpers hook
  const helpers = useCurrencyHelpers({ currencies, accounts, defaultCurrency: defaultCurrency || "USD" });

  // Drag and drop hook
  const { sensors, handleCurrencyDragEnd } = useCurrencyDragAndDrop({
    workspaceId,
    currencies,
    setCurrencies,
    setSnackbarElement: uiState.setSnackbarElement,
    setSnackbarOpen: uiState.setSnackbarOpen,
  });

  // Handlers hook
  const handlers = useCurrencyHandlers({
    workspaceId,
    defaultCurrency: defaultCurrency || "USD",
    currencies,
    setDefaultCurrency,
    setCurrencies,
    fetchExchangeRatesFromAPI: helpers.fetchExchangeRatesFromAPI,
    formatRate: helpers.formatRate,
    setAddDialogOpen: uiState.setAddDialogOpen,
    setNewCurrencyCode: uiState.setNewCurrencyCode,
    setAddingCurrency: uiState.setAddingCurrency,
    setEditDialogOpen: uiState.setEditDialogOpen,
    setEditingCurrency: uiState.setEditingCurrency,
    setEditCurrencyRate: uiState.setEditCurrencyRate,
    setDeleteDialogOpen: uiState.setDeleteDialogOpen,
    setCurrencyToDelete: uiState.setCurrencyToDelete,
    setDefaultCurrencyDialogOpen: uiState.setDefaultCurrencyDialogOpen,
    setSelectedDefaultCurrency: uiState.setSelectedDefaultCurrency,
    setRefreshDialogOpen: uiState.setRefreshDialogOpen,
    setCurrencyToRefresh: uiState.setCurrencyToRefresh,
    setRefreshingCurrency: uiState.setRefreshingCurrency,
    setFetchingRateFromAPI: uiState.setFetchingRateFromAPI,
    setFetchedRateFromAPI: uiState.setFetchedRateFromAPI,
    setSnackbarOpen: uiState.setSnackbarOpen,
    setSnackbarElement: uiState.setSnackbarElement,
    newCurrencyCode: uiState.newCurrencyCode,
    editingCurrency: uiState.editingCurrency,
    editCurrencyRate: uiState.editCurrencyRate,
    currencyToDelete: uiState.currencyToDelete,
    currencyToRefresh: uiState.currencyToRefresh,
    fetchedRateFromAPI: uiState.fetchedRateFromAPI,
    refreshingCurrency: uiState.refreshingCurrency,
    editingDecimalPlacesCurrency: uiState.editingDecimalPlacesCurrency,
    decimalPlaces: uiState.decimalPlaces,
    updatingDecimalPlaces: uiState.updatingDecimalPlaces,
    setEditDecimalPlacesDialogOpen: uiState.setEditDecimalPlacesDialogOpen,
    setEditingDecimalPlacesCurrency: uiState.setEditingDecimalPlacesCurrency,
    setDecimalPlaces: uiState.setDecimalPlaces,
    setUpdatingDecimalPlaces: uiState.setUpdatingDecimalPlaces,
  });

  return (
    <Box sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0, height: "100%", bgcolor: "background.default" }}>
      <Paper
        elevation={0}
        sx={{
          borderBottom: 1,
          borderColor: "divider",
          p: isMobile ? 1.5 : 2,
          position: "sticky",
          top: 0,
          zIndex: 10,
          bgcolor: "background.paper",
        }}
      >
        {!isLoadingDefaultCurrency && defaultCurrency && (
          <Box 
            sx={{ 
              mb: 2, 
              p: 1.5, 
              border: 1, 
              borderColor: "divider", 
              borderRadius: 1,
              bgcolor: "background.paper",
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              alignItems: isMobile ? "stretch" : "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 1.5,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap", flex: 1 }}>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                Default Currency:
              </Typography>
              <Chip
                label={defaultCurrency}
                color="primary"
                size="medium"
                sx={{ 
                  fontWeight: 600,
                  fontSize: "0.875rem",
                  height: "auto",
                  py: 0.5,
                }}
              />
              <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                Decimals: {defaultCurrencyDecimalPlaces}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", flexDirection: isMobile ? "column" : "row" }}>
              {canEditDefaultDecimals() && (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<EditIcon />}
                  onClick={handlers.handleEditDefaultCurrencyDecimalPlaces}
                  fullWidth={isMobile}
                >
                  Edit Decimals
                </Button>
              )}
              {canEditDefaultCurrency() && (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<EditIcon />}
                  onClick={handlers.handleOpenDefaultCurrencyDialog}
                  fullWidth={isMobile}
                >
                  Edit Default Currency
                </Button>
              )}
            </Box>
          </Box>
        )}
        <Alert severity="info" sx={{ mb: 0 }}>
          The default currency is used for statistics and graph previews. All transactions maintain their original currency.
        </Alert>
      </Paper>

      <Box
        ref={scrollContainerRef}
        sx={{
          flex: 1,
          overflowY: "auto",
          overflowX: "hidden",
          p: 0,
          WebkitOverflowScrolling: "touch",
        }}
      >
        {loading ? (
          <LoadingSpinner fullPage={false} />
        ) : (
          <CurrencyTable
            currencies={currencies}
            defaultCurrency={defaultCurrency || "USD"}
            helpers={helpers}
            handlers={handlers}
            onAddCurrencyClick={() => uiState.setAddDialogOpen(true)}
            sensors={sensors}
            onDragEnd={handleCurrencyDragEnd}
            isMobile={isMobile}
            onDragStateChange={isMobile ? setIsDraggingCurrency : undefined}
            canAddCurrency={canAddCurrency()}
            canChangeDefaultCurrency={canEditDefaultCurrency()}
            canChangeCurrencyRateDecimalPlaces={canEditDefaultDecimals()}
          />
        )}
      </Box>

      {/* Dialogs */}
      <AddCurrencyDialog
        open={uiState.addDialogOpen}
        onClose={() => {
          if (!uiState.addingCurrency) {
            uiState.setAddDialogOpen(false);
            uiState.setNewCurrencyCode(null);
            uiState.setDecimalPlaces(2); // Reset to default
          }
        }}
        onAdd={handlers.handleAddCurrency}
        newCurrencyCode={uiState.newCurrencyCode}
        setNewCurrencyCode={uiState.setNewCurrencyCode}
        addingCurrency={uiState.addingCurrency}
        getAvailableCurrencies={helpers.getAvailableCurrencies}
        decimalPlaces={uiState.decimalPlaces}
        setDecimalPlaces={uiState.setDecimalPlaces}
      />

      <EditRateDialog
        open={uiState.editDialogOpen}
        onClose={() => {
          uiState.setEditDialogOpen(false);
          uiState.setEditingCurrency(null);
          uiState.setEditCurrencyRate("1.0");
        }}
        onUpdate={handlers.handleUpdateRate}
        editingCurrency={uiState.editingCurrency}
        editCurrencyRate={uiState.editCurrencyRate}
        setEditCurrencyRate={uiState.setEditCurrencyRate}
        defaultCurrency={defaultCurrency || "USD"}
        formatRate={helpers.formatRate}
      />

      <EditDecimalPlacesDialog
        open={uiState.editDecimalPlacesDialogOpen}
        onClose={() => {
          if (!uiState.updatingDecimalPlaces) {
            uiState.setEditDecimalPlacesDialogOpen(false);
            uiState.setEditingDecimalPlacesCurrency(null);
            uiState.setDecimalPlaces(2); // Reset to default
          }
        }}
        onUpdate={handlers.handleUpdateDecimalPlaces}
        editingCurrency={uiState.editingDecimalPlacesCurrency}
        decimalPlaces={uiState.decimalPlaces}
        setDecimalPlaces={uiState.setDecimalPlaces}
        updating={uiState.updatingDecimalPlaces}
      />

      <DeleteConfirmationDialog
        open={uiState.deleteDialogOpen}
        onClose={() => {
          uiState.setDeleteDialogOpen(false);
          uiState.setCurrencyToDelete("");
        }}
        onConfirm={handlers.handleDeleteConfirm}
        itemName={uiState.currencyToDelete}
        itemType="currency"
        warningMessage={
          helpers.isCurrencyInUse(uiState.currencyToDelete) ? (
            <Box>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                Can't delete currency while it's used in account or card.
              </Typography>
              <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
                This currency is used in:
              </Typography>
              {helpers.getAccountsUsingCurrency(uiState.currencyToDelete).map((acc) => (
                <Typography key={acc.id} variant="caption" display="block">
                  • {acc.accountName} ({acc.group})
                </Typography>
              ))}
              <Typography variant="caption" sx={{ mt: 0.5, display: "block" }}>
                Remove or change currency from all accounts to delete.
              </Typography>
            </Box>
          ) : undefined
        }
        disabled={helpers.isCurrencyInUse(uiState.currencyToDelete)}
        showWarningAsAlert={false}
      />

      <RefreshRateDialog
        open={uiState.refreshDialogOpen}
        onClose={handlers.handleCloseRefreshDialog}
        onConfirm={handlers.handleRefreshRateConfirm}
        currencyToRefresh={uiState.currencyToRefresh}
        defaultCurrency={defaultCurrency || "USD"}
        fetchedRateFromAPI={uiState.fetchedRateFromAPI}
        fetchingRateFromAPI={uiState.fetchingRateFromAPI}
        refreshingCurrency={uiState.refreshingCurrency}
        formatRate={helpers.formatRate}
      />

      <DefaultCurrencyDialog
        open={uiState.defaultCurrencyDialogOpen}
        onClose={() => {
          uiState.setDefaultCurrencyDialogOpen(false);
          uiState.setSelectedDefaultCurrency("");
        }}
        onSetDefault={handlers.handleSetDefault}
        selectedDefaultCurrency={uiState.selectedDefaultCurrency}
        setSelectedDefaultCurrency={uiState.setSelectedDefaultCurrency}
        currentDefaultCurrency={defaultCurrency || "USD"}
      />

      {/* Snackbar for demo mode alerts */}
      <Snackbar
        open={uiState.snackbarOpen}
        autoHideDuration={6000}
        onClose={() => {
          uiState.setSnackbarOpen(false);
          uiState.setSnackbarElement(null);
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={() => {
          uiState.setSnackbarOpen(false);
          uiState.setSnackbarElement(null);
        }} severity="info" sx={{ width: "100%" }}>
          {uiState.snackbarElement || uiState.snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default CurrencySettings;
